import React from "react";

import { css } from "@emotion/react";
import { ReactTFC } from "../../utils/fp/missing-ts-parts";
import { CSS_COLORS } from "../../styles/styles-css-variables";
import { CSS_MQ } from "../../styles/breakpoints";
import { ContentContainer } from "../_shared-components/arrangement/Container";
import { ContentfulComponentCMSHook } from "@swo/shared";
import { HeadlineAndTextType } from "@swo/storefront";
import { SwarovskiLink } from "../_shared-components/ui-elements/SwarovskiLink";

import cn from "classnames";
import { fontColor, FONTS, fontSize } from "../../styles/fonts";
import { getComponentCMSClassname } from "../cms/edit-overlay/component-hook-class";
import { plainTextStyles } from "../../styles/text-styles";
import { SORichTextRenderer } from "../_shared-components/so-rich-text";

export const HeadlineAndTextComponent: ReactTFC<HeadlineAndTextType & ContentfulComponentCMSHook> = props => {
  const withMarginLeft = props.alignment === "Right" || props.alignment === "Center";
  const withMarginRight = props.alignment === "Left" || props.alignment === "Center";
  const widthClassName = getWidthClassName(props.width);

  return (
    <ContentContainer className={getComponentCMSClassname(props?.sys?.id)}>
      <div
        className={cn({ "with-margin-left": withMarginLeft, "with-margin-right": withMarginRight }, widthClassName)}
        css={css`
          word-break: break-word;

          &.with-margin-left {
            margin-left: auto;
          }

          &.with-margin-right {
            margin-right: auto;
          }

          ${CSS_MQ.atLeastMd} {
            width: calc(100% * 2 / 3);

            &.width-full {
              width: 100%;
            }
          }

          ${CSS_MQ.atLeastLg} {
            &.width-33 {
              width: calc(100% / 3);
            }

            &.width-50 {
              width: 50%;
            }

            &.width-full {
              width: 100%;
            }
          }
        `}
      >
        <h3
          css={[
            FONTS.MinervaModern500,
            fontSize.x4,
            css`
              margin-top: 0;
              margin-bottom: 20px;

              color: ${CSS_COLORS.Green};
              text-transform: uppercase;

              .with-margin-left.with-margin-right > & {
                text-align: center;
              }
            `,
          ]}
        >
          {props.headline}
        </h3>
        <div
          css={[
            FONTS.SwarovskiFutura300,
            fontColor.black,
            plainTextStyles,
            css`
              p:last-child {
                margin-bottom: 0;
              }
            `,
          ]}
        >
          <SORichTextRenderer richText={props.richText} options={"with-hyperlinks"} />
        </div>

        {props.callToAction &&
          (props.callToAction.type === "Internal Link" ? (
            <SwarovskiLink link={props.callToAction.data} isInternal={true} scrollToTop={true} styled={"underline"}>
              {props.callToAction.label}
            </SwarovskiLink>
          ) : (
            <SwarovskiLink
              link={props.callToAction.link}
              isInternal={false}
              openInNewTab={!!props.callToAction.openInNewTab}
              styled={"underline"}
            >
              {props.callToAction.label}
            </SwarovskiLink>
          ))}
      </div>
    </ContentContainer>
  );
};

function getWidthClassName(width: HeadlineAndTextType["width"]): "width-50" | "width-33" | "width-full" {
  switch (width) {
    case "1 / 2 Width": {
      return "width-50";
    }

    case "1 / 3 Width": {
      return "width-33";
    }

    case "Content": {
      return "width-full";
    }

    default: {
      const _: never = width;

      return _;
    }
  }
}
